import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface BettingPanelProps {
  balance: number;
  bet: number;
  onBet: (amount: number) => void;
  gameActive: boolean;
}

export default function BettingPanel({
  balance,
  bet,
  onBet,
  gameActive,
}: BettingPanelProps) {
  const [customBet, setCustomBet] = useState('');

  const chipValues = [10, 50, 100, 500, 1000, 5000];

  const handleChipClick = (value: number) => {
    if (!gameActive && bet + value <= balance) {
      onBet(bet + value);
    }
  };

  const handleCustomBet = () => {
    const amount = parseInt(customBet);
    if (!isNaN(amount) && amount > 0 && amount <= balance) {
      onBet(amount);
      setCustomBet('');
    }
  };

  const clearBet = () => {
    onBet(0);
  };

  return (
    <Card className="p-4 md:p-6 bg-gradient-to-b from-slate-900 to-slate-800 border-2 border-amber-700 m-4 md:m-0">
      <h3 className="text-base md:text-lg font-bold text-white mb-4 md:mb-6">PLACE YOUR BET</h3>

      {/* Balance Display */}
      <div className="mb-4 md:mb-6 p-3 md:p-4 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg border-2 border-blue-400">
        <p className="text-xs md:text-sm text-blue-100">Demo Balance</p>
        <p className="text-2xl md:text-3xl font-bold text-yellow-300">${balance.toLocaleString()}</p>
      </div>

      {/* Current Bet Display */}
      <div className="mb-4 md:mb-6 p-3 md:p-4 bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg border-2 border-purple-400">
        <p className="text-xs md:text-sm text-purple-100">Total Bet</p>
        <p className="text-2xl md:text-3xl font-bold text-yellow-300">${bet.toLocaleString()}</p>
      </div>

      {/* Chip Buttons */}
      <div className="mb-4 md:mb-6">
        <p className="text-xs md:text-sm font-semibold text-white mb-2 md:mb-3">Quick Bet</p>
        <div className="grid grid-cols-3 md:grid-cols-2 gap-2 md:gap-3">
          {chipValues.map((value) => (
            <button
              key={value}
              onClick={() => handleChipClick(value)}
              disabled={gameActive || bet + value > balance}
              className={`py-2 md:py-3 px-2 md:px-3 rounded-lg font-bold text-xs md:text-sm transition transform hover:scale-105 chip-drop ${
                bet + value > balance
                  ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  : 'bg-gradient-to-b from-amber-400 to-amber-600 text-white hover:shadow-2xl cursor-pointer active:scale-95'
              }`}
            >
              ${value.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Bet */}
      <div className="mb-4 md:mb-6">
        <p className="text-xs md:text-sm font-semibold text-white mb-2">Custom Amount</p>
        <div className="flex gap-2">
          <input
            type="number"
            value={customBet}
            onChange={(e) => setCustomBet(e.target.value)}
            disabled={gameActive}
            placeholder="Enter amount"
            className="flex-1 px-2 md:px-3 py-1 md:py-2 border-2 border-amber-600 rounded-lg text-xs md:text-sm bg-slate-700 text-white"
          />
          <Button
            onClick={handleCustomBet}
            disabled={gameActive || !customBet}
            className="bg-green-600 hover:bg-green-700"
            size="sm"
          >
            Add
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-2 md:space-y-3">
        <Button
          onClick={clearBet}
          disabled={gameActive || bet === 0}
          variant="outline"
          className="w-full border-red-500 text-red-500 hover:bg-red-600 hover:text-white text-xs md:text-sm"
        >
          Clear Bet
        </Button>
      </div>
    </Card>
  );
}